import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'xs'|'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'sm',
  className
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

useEffect(() => {
  if (isOpen) {
    // Use multiple event types to ensure it works
    const handleKeyEvent = (e: KeyboardEvent) => {
      if (e.key === 'Escape'|| e.key === 'Esc' || e.keyCode === 27 || e.which === 27) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        onClose();
        return false;
      }
    };

    // Add to multiple event types
    ['keydown', 'keyup', 'keypress'].forEach(eventType => {
      document.addEventListener(eventType, handleKeyEvent, true);
      window.addEventListener(eventType, handleKeyEvent, true);
    });

    document.body.style.overflow = 'hidden';

    return () => {
      ['keydown', 'keyup', 'keypress'].forEach(eventType => {
        document.removeEventListener(eventType, handleKeyEvent, true);
        window.removeEventListener(eventType, handleKeyEvent, true);
      });
      document.body.style.overflow = 'auto';
    };
  }
}, [isOpen, onClose]);

  if (!isOpen) return null;

  const sizeClasses = {
    xs:'max-w-sm',
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl'
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal Content */}
        <div
          ref={modalRef}
          className={clsx(
            'relative bg-white rounded-lg shadow-xl w-full',
            sizeClasses[size],
            className
          )}
        >
          {/* Header */}
         {title && <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>}

          {/* Body */}
          <div className={clsx("py-4",className)}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};